/**
 * Spec §8 A10 — customer cancel fulfillment (snapshot money evaluated on backend).
 * Hidden when A11 pending-unavailable decision panel owns the cancel-all path.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button.js';
import { ConfirmDialog } from '../../../shared/ui/ConfirmDialog.js';
import type { UseOrderLiveStatusResult } from '../hooks/useOrderLiveStatus.js';

export interface FulfillmentCancelPanelProps {
  readonly fulfillmentId: number;
  readonly live: Pick<UseOrderLiveStatusResult, 'cancelFulfillment' | 'error'>;
  readonly statusLabel?: string;
}

export function FulfillmentCancelPanel({
  fulfillmentId,
  live,
  statusLabel,
}: FulfillmentCancelPanelProps): JSX.Element {
  const { t } = useTranslation('customer');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const onConfirm = async (): Promise<void> => {
    setBusy(true);
    setLocalError(null);
    try {
      await live.cancelFulfillment({ fulfillmentId });
      setConfirmOpen(false);
    } catch (err) {
      setLocalError(
        err instanceof Error
          ? err.message
          : t('account.orders.a10.submitError', {
              defaultValue: 'Could not cancel this order. Retry in a moment.',
            })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
      data-testid="fulfillment-cancel-panel"
      aria-labelledby="fulfillment-cancel-title"
    >
      <h3
        id="fulfillment-cancel-title"
        className="m-0 text-base font-semibold text-[var(--color-text)]"
      >
        {t('account.orders.a10.title', { defaultValue: 'Cancel this order' })}
      </h3>
      <p className="mt-2 m-0 text-sm text-[var(--color-text-muted)]">
        {t('account.orders.a10.body', {
          defaultValue:
            'Refund follows the policy locked at checkout (ordinary goods vs prepared items). Prepared items may not refund after prep has started.',
        })}
      </p>
      {statusLabel ? (
        <p className="mt-1 m-0 text-xs text-[var(--color-text-muted)]" data-testid="a10-live-status">
          {statusLabel}
        </p>
      ) : null}
      {(localError ?? live.error) != null ? (
        <p className="mt-2 m-0 text-sm text-[var(--color-danger)]" role="alert">
          {localError ?? live.error}
        </p>
      ) : null}
      <div className="mt-4">
        <Button
          type="button"
          intent="secondary"
          data-testid="a10-cancel-order"
          disabled={busy}
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          {t('account.orders.a10.cancelCta', { defaultValue: 'Cancel order' })}
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={t('account.orders.a10.confirmTitle', { defaultValue: 'Cancel this order?' })}
        description={t('account.orders.a10.confirmDescription', {
          defaultValue:
            'This cannot be undone. Any refund will follow the checkout policy snapshot for this order.',
        })}
        confirmLabel={t('account.orders.a10.confirmCta', { defaultValue: 'Yes, cancel order' })}
        cancelLabel={t('shared.cancel', { defaultValue: 'Keep order' })}
        pending={busy}
        confirmIntent="danger"
        testId="a10-cancel-confirm"
        onCancel={() => {
          if (!busy) {
            setConfirmOpen(false);
          }
        }}
        onConfirm={() => {
          void onConfirm();
        }}
      />
    </section>
  );
}
